import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';

const Home = () => {
  const { user } = useAuth();

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Small Business Owner",
      text: "Saved me $3,000 in legal fees! Found 3 critical issues in my lease agreement before I signed.",
      rating: 5
    },
    {
      name: "David Chen",
      role: "Freelance Designer",
      text: "Caught unfair termination clauses in my client contract. ContractGuard paid for itself 10x over.",
      rating: 5
    },
    {
      name: "Emily Rodriguez",
      role: "Property Buyer",
      text: "Used it for my home purchase contract. The AI spotted things my realtor missed. Incredible!",
      rating: 5
    }
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-primary-600 to-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              AI Finds Hidden Risks in Your Contracts
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-primary-100">
              Before you sign that lease, employment contract, or property agreement...
            </p>
            <p className="text-lg mb-8 text-primary-200">
              Let AI scan for unfair clauses, hidden fees, and legal pitfalls in under 60 seconds
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              {user ? (
                <Link
                  to="/upload"
                  className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg shadow-lg"
                >
                  Upload Contract Now →
                </Link>
              ) : (
                <>
                  <Link
                    to="/signup"
                    className="bg-white text-primary-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-bold text-lg shadow-lg"
                  >
                    Try Free Scan Now →
                  </Link>
                  <Link
                    to="/pricing"
                    className="bg-primary-500 hover:bg-primary-400 px-8 py-4 rounded-lg font-semibold text-lg border-2 border-white"
                  >
                    View Pricing
                  </Link>
                </>
              )}
            </div>
            <p className="text-sm text-primary-200">✓ No credit card required  •  ✓ 1 free scan  •  ✓ Results in 60 seconds</p>
            <p className="text-xs text-primary-300 mt-2">🔒 Your privacy matters: We don't cache or store your contract content beyond what's necessary to provide analysis</p>
          </div>
        </div>
      </div>

      {/* Social Proof */}
      <div className="bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-600">10,000+</div>
              <div className="text-gray-600 mt-2">Contracts Analysed</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">$2.5M+</div>
              <div className="text-gray-600 mt-2">Saved in Legal Fees</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">4.9/5</div>
              <div className="text-gray-600 mt-2">Average Rating</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">60 Sec</div>
              <div className="text-gray-600 mt-2">Analysis Time</div>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h2 className="text-4xl font-bold text-center mb-4">What ContractGuard Finds For You</h2>
        <p className="text-center text-gray-600 mb-12">Our AI scans every clause to protect your interests</p>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="bg-white border-2 border-red-200 rounded-lg p-6 hover:shadow-lg transition">
            <div className="text-4xl mb-4">🚨</div>
            <h3 className="text-xl font-semibold mb-2">Hidden Risks</h3>
            <p className="text-gray-600">Unfair termination clauses, auto-renewals, and liability traps</p>
          </div>

          <div className="bg-white border-2 border-yellow-200 rounded-lg p-6 hover:shadow-lg transition">
            <div className="text-4xl mb-4">💰</div>
            <h3 className="text-xl font-semibold mb-2">Hidden Costs</h3>
            <p className="text-gray-600">Surprise fees, penalties, and unfavorable payment terms</p>
          </div>

          <div className="bg-white border-2 border-blue-200 rounded-lg p-6 hover:shadow-lg transition">
            <div className="text-4xl mb-4">⚖️</div>
            <h3 className="text-xl font-semibold mb-2">Legal Issues</h3>
            <p className="text-gray-600">Missing protections, ambiguous terms, and non-compete issues</p>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">What Our Users Say</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div key={idx} className="bg-gray-800 rounded-lg p-6">
                <div className="flex mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{testimonial.text}"</p>
                <div>
                  <div className="font-semibold">{testimonial.name}</div>
                  <div className="text-gray-400 text-sm">{testimonial.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">How It Works</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold mb-2">Upload Your Contract</h3>
              <p className="text-gray-600">PDF, DOC, or DOCX - any contract type</p>
            </div>

            <div className="text-center">
              <div className="bg-primary-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold mb-2">AI Analyses Every Clause</h3>
              <p className="text-gray-600">Scans for risks, costs, and legal issues</p>
            </div>

            <div className="text-center">
              <div className="bg-primary-600 text-white rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold mb-2">Get Actionable Insights</h3>
              <p className="text-gray-600">Detailed report with recommendations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Don't Sign Without Knowing What's Inside</h2>
          <p className="text-xl mb-8 text-primary-100">
            Join 10,000+ people who scan their contracts before signing
          </p>
          <Link
            to={user ? "/upload" : "/signup"}
            className="bg-white text-primary-600 hover:bg-gray-100 px-10 py-4 rounded-lg font-bold text-xl inline-block shadow-xl"
          >
            {user ? "Scan Your Contract Now →" : "Get Your Free Scan →"}
          </Link>
          <p className="mt-6 text-primary-200">No credit card required • Takes 60 seconds</p>
        </div>
      </div>
    </div>
  );
};

export default Home;
